import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bot, 
  Search, 
  Zap, 
  BarChart3, 
  Rocket, 
  Brain, 
  MessageCircle, 
  Code, 
  Users, 
  DollarSign, 
  Star, 
  ArrowRight, 
  CheckCircle, 
  Github, 
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { AIAssistant } from '../components/AIAssistant';
import { Twitter, Linkedin } from '../components/Icons';

export function LandingPage() {
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantInitialMessage, setAssistantInitialMessage] = useState('');

  // Show the assistant automatically after a short delay for new visitors
  useEffect(() => {
    const hasSeenAssistant = localStorage.getItem('hasSeenAssistant');
    if (!hasSeenAssistant) {
      const timer = setTimeout(() => {
        setAssistantInitialMessage("Hi there! I'm your AI assistant for Agent Store. I can help you learn about our platform, discover AI agents, and answer any questions you have. How can I help you today?");
        setAssistantOpen(true);
        localStorage.setItem('hasSeenAssistant', 'true');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-xl border-b border-dark-200/30 sticky top-0 z-30 shadow-soft">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-primary-600 to-accent-600 rounded-xl shadow-medium">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  Agent Store
                </h1>
                <p className="text-dark-600 text-sm font-medium">Discover AI agents from across the web</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2 text-dark-700 hover:text-dark-900 hover:bg-dark-50 rounded-lg transition-all duration-200 font-medium"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="btn-primary"
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6 text-dark-900">
                <span className="bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  Discover, Deploy, and Monetize
                </span>
                <br />
                AI Agents with Ease
              </h1>
              <p className="text-xl text-dark-700 mb-8 leading-relaxed">
                Your hub for finding the perfect AI agents, connecting them together, and even selling your own creations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup"
                  className="px-8 py-4 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-xl hover:from-primary-700 hover:to-accent-700 transition-all duration-200 shadow-medium hover:shadow-strong hover:scale-105 font-semibold text-center"
                >
                  Get Started Free
                </Link>
                <Link
                  to="/login"
                  className="px-8 py-4 border-2 border-dark-300 text-dark-700 rounded-xl hover:bg-dark-50 hover:border-primary-300 transition-all duration-200 text-center font-medium"
                >
                  Explore Agents
                </Link>
              </div>
            </div>
            <div className="md:w-1/2 relative">
              <div className="relative z-10 bg-white rounded-2xl shadow-strong overflow-hidden border-2 border-dark-200">
                <div className="bg-gradient-to-r from-primary-600 to-accent-600 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    <h3 className="font-semibold">Agent Discovery</h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-primary-50 rounded-lg p-4 flex items-center gap-3 border border-primary-200">
                      <div className="p-2 bg-primary-100 rounded-lg">
                        <Bot className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-dark-900">CustomerBot Pro</div>
                        <div className="text-xs text-dark-500">Customer Support</div>
                      </div>
                    </div>
                    <div className="bg-accent-50 rounded-lg p-4 flex items-center gap-3 border border-accent-200">
                      <div className="p-2 bg-accent-100 rounded-lg">
                        <Code className="w-5 h-5 text-accent-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-dark-900">CodeAssist AI</div>
                        <div className="text-xs text-dark-500">Development</div>
                      </div>
                    </div>
                    <div className="bg-primary-50 rounded-lg p-4 flex items-center gap-3 border border-primary-200">
                      <div className="p-2 bg-primary-100 rounded-lg">
                        <BarChart3 className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-dark-900">DataInsight Pro</div>
                        <div className="text-xs text-dark-500">Data Analysis</div>
                      </div>
                    </div>
                    <div className="bg-accent-50 rounded-lg p-4 flex items-center gap-3 border border-accent-200">
                      <div className="p-2 bg-accent-100 rounded-lg">
                        <Sparkles className="w-5 h-5 text-accent-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-dark-900">ContentGenie</div>
                        <div className="text-xs text-dark-500">Content Creation</div>
                      </div>
                    </div>
                  </div>
                  <button className="w-full py-2 bg-dark-100 text-dark-700 rounded-lg hover:bg-dark-200 transition-all duration-200 text-sm font-medium">
                    View All Agents
                  </button>
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-r from-primary-600/20 to-accent-600/20 rounded-[30px] blur-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-dark-900">Powerful Platform Features</h2>
            <p className="text-xl text-dark-600 max-w-3xl mx-auto">
              Everything you need to discover, analyze, deploy, and monetize AI agents in one place
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card-modern p-8 hover:shadow-strong hover:border-primary-300 hover:-translate-y-1 transition-all duration-300">
              <div className="p-3 bg-primary-100 rounded-xl w-14 h-14 flex items-center justify-center mb-6">
                <Search className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-dark-900">Agent Discovery</h3>
              <p className="text-dark-600 mb-4 leading-relaxed">
                Find the perfect AI agents from GitHub, Hugging Face, and other sources with powerful search and filtering.
              </p>
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all duration-200">
                Explore Agents <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="card-modern p-8 hover:shadow-strong hover:border-primary-300 hover:-translate-y-1 transition-all duration-300">
              <div className="p-3 bg-accent-100 rounded-xl w-14 h-14 flex items-center justify-center mb-6">
                <Brain className="w-7 h-7 text-accent-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-dark-900">Agent Analysis</h3>
              <p className="text-dark-600 mb-4 leading-relaxed">
                Get detailed insights into agent capabilities, complexity, and setup requirements before deployment.
              </p>
              <Link to="/login" className="text-accent-600 hover:text-accent-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all duration-200">
                Analyze Agents <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="card-modern p-8 hover:shadow-strong hover:border-primary-300 hover:-translate-y-1 transition-all duration-300">
              <div className="p-3 bg-primary-100 rounded-xl w-14 h-14 flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-dark-900">UI Generation</h3>
              <p className="text-dark-600 mb-4 leading-relaxed">
                Automatically create interactive interfaces for code-based agents with just a few clicks.
              </p>
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all duration-200">
                Generate UIs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="card-modern p-8 hover:shadow-strong hover:border-primary-300 hover:-translate-y-1 transition-all duration-300">
              <div className="p-3 bg-accent-100 rounded-xl w-14 h-14 flex items-center justify-center mb-6">
                <Rocket className="w-7 h-7 text-accent-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-dark-900">One-Click Deployment</h3>
              <p className="text-dark-600 mb-4 leading-relaxed">
                Deploy agents to production environments with streamlined workflows and configuration.
              </p>
              <Link to="/login" className="text-accent-600 hover:text-accent-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all duration-200">
                Deploy Agents <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="card-modern p-8 hover:shadow-strong hover:border-primary-300 hover:-translate-y-1 transition-all duration-300">
              <div className="p-3 bg-dark-100 rounded-xl w-14 h-14 flex items-center justify-center mb-6">
                <MessageCircle className="w-7 h-7 text-dark-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-dark-900">A2A Protocol</h3>
              <p className="text-dark-600 mb-4 leading-relaxed">
                Connect agents together using the Agent-to-Agent protocol for powerful multi-agent systems.
              </p>
              <Link to="/login" className="text-dark-600 hover:text-dark-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all duration-200">
                Explore A2A <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="card-modern p-8 hover:shadow-strong hover:border-primary-300 hover:-translate-y-1 transition-all duration-300">
              <div className="p-3 bg-primary-100 rounded-xl w-14 h-14 flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-dark-900">Agent Marketplace</h3>
              <p className="text-dark-600 mb-4 leading-relaxed">
                Buy, sell, and subscribe to premium agents with secure payments and subscription management.
              </p>
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1 hover:gap-2 transition-all duration-200">
                Visit Marketplace <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* A2A Protocol Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full font-medium text-sm mb-6">
                New Feature
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Connect Agents Together with A2A Protocol
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Build powerful multi-agent systems using our implementation of Google's Agent-to-Agent (A2A) protocol. Enable seamless communication between specialized agents to solve complex problems.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-indigo-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Standardized Communication</h4>
                    <p className="text-gray-600">Consistent message format for reliable agent interactions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-indigo-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Agent Discovery</h4>
                    <p className="text-gray-600">Find agents with specific capabilities for your workflows</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-indigo-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Visual Workflow Designer</h4>
                    <p className="text-gray-600">Create complex multi-agent systems with our intuitive designer</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/login"
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-lg font-medium"
                >
                  Explore A2A Protocol
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-indigo-100">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    <h3 className="font-medium">A2A Protocol</h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="bg-gray-900 rounded-lg p-4 text-sm font-mono text-green-400 mb-4 overflow-x-auto">
                    <pre>{`{
  "sender": {
    "agent_id": "data-analysis-agent",
    "name": "Data Analysis Agent"
  },
  "recipient": {
    "agent_id": "visualization-agent",
    "name": "Visualization Agent"
  },
  "message_type": "request",
  "content": {
    "action": "create_visualization",
    "data": [
      {"category": "A", "value": 10},
      {"category": "B", "value": 15},
      {"category": "C", "value": 7}
    ],
    "visualization_type": "bar_chart"
  }
}`}</pre>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MessageCircle className="w-4 h-4" />
                      <span>A2A Message Format</span>
                    </div>
                    <Link
                      to="/login"
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                    >
                      View Documentation
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section with Images */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Agent Marketplace</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Buy, sell, and subscribe to premium AI agents from creators around the world
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/7567434/pexels-photo-7567434.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Customer Support Bot" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-xl">CustomerBot Pro</h3>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">$19.99</span>
                </div>
                <p className="text-gray-600 mb-4">
                  Advanced customer support chatbot with sentiment analysis and multi-language support.
                </p>
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span>4.8 (234)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>1,250+</span>
                  </div>
                </div>
                <button className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors">
                  View Details
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/7567620/pexels-photo-7567620.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Data Analysis Tool" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-xl">DataInsight Pro</h3>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">$14.99</span>
                </div>
                <p className="text-gray-600 mb-4">
                  Automated data analysis and visualization with predictive analytics capabilities.
                </p>
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span>4.7 (156)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>567+</span>
                  </div>
                </div>
                <button className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors">
                  View Details
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/4164418/pexels-photo-4164418.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Code Review Assistant" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-xl">CodeReview Assistant</h3>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Free</span>
                </div>
                <p className="text-gray-600 mb-4">
                  AI-powered code review and optimization suggestions for development teams.
                </p>
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span>4.9 (298)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>2,100+</span>
                  </div>
                </div>
                <button className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors">
                  View Details
                </button>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-xl hover:from-primary-700 hover:to-accent-700 transition-all duration-200 shadow-medium hover:shadow-strong hover:scale-105 font-semibold"
            >
              Explore Marketplace <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* For Developers Section with Image */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full font-medium text-sm mb-6">
                For Developers
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Monetize Your AI Agents
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Turn your AI creations into a revenue stream. List your agents on our marketplace and earn money when users purchase or subscribe to them.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-purple-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Flexible Pricing Models</h4>
                    <p className="text-gray-600">Choose from one-time purchases or subscription-based pricing</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-purple-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">70% Revenue Share</h4>
                    <p className="text-gray-600">Keep 70% of all sales revenue from your agents</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-purple-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Built-in Distribution</h4>
                    <p className="text-gray-600">Reach customers without building your own platform</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/login"
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors shadow-lg font-medium"
                >
                  Start Selling Your Agents
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Developer working on AI agents" 
                  className="rounded-2xl shadow-xl border-4 border-white"
                />
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl overflow-hidden border border-purple-100 w-64">
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      <h3 className="font-medium">Revenue Dashboard</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-purple-600">$1,247</div>
                    <div className="text-sm text-gray-500">Monthly Revenue</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Trusted by Developers and Users</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our growing community of AI agent creators and users
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">2,500+</div>
              <p className="text-gray-600">AI Agents</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">15K+</div>
              <p className="text-gray-600">Active Users</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">500+</div>
              <p className="text-gray-600">Agent Developers</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-600 mb-2">50K+</div>
              <p className="text-gray-600">Deployments</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="py-20 px-6 relative">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/8386434/pexels-photo-8386434.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="AI Background" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-90"></div>
        </div>
        <div className="container mx-auto max-w-6xl text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to Get Started?</h2>
          <p className="text-xl text-white opacity-90 max-w-3xl mx-auto mb-8">
            Join thousands of users discovering and deploying AI agents every day
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl hover:bg-gray-100 transition-colors shadow-lg font-medium"
            >
              Create Free Account
            </Link>
            <Link
              to="/login"
              className="px-8 py-4 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-colors font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Bot className="w-6 h-6" />
                </div>
                <div className="font-bold text-xl">Agent Store</div>
              </div>
              <p className="text-gray-400 mb-4">
                Your hub for discovering, deploying, and monetizing AI agents
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Github className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-lg mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Agent Discovery</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Agent Analysis</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">UI Generation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Deployment</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">A2A Protocol</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-lg mb-4">Developers</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Marketplace</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Developer Portal</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-lg mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>© 2025 Agent Store. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* AI Assistant */}
      <AIAssistant 
        isOpen={assistantOpen} 
        onToggle={() => setAssistantOpen(!assistantOpen)}
        initialMessage={assistantInitialMessage}
        setInitialMessage={setAssistantInitialMessage}
      />

      {/* Chat button - only show if assistant is closed */}
      {!assistantOpen && (
        <button
          onClick={() => {
            setAssistantInitialMessage("Hi! I'm your AI assistant for Agent Store. How can I help you today?");
            setAssistantOpen(true);
          }}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-full shadow-strong hover:shadow-xl transition-all duration-300 flex items-center justify-center z-50 hover:scale-110"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}